<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WabaCallingAPI from './api';

const props = defineProps({ inbox: { type: Object, required: true } });

const { t } = useI18n();
const enabled = ref(false);
const managedExternally = ref(false);
const loading = ref(true);
const saving = ref(false);

const load = async () => {
  loading.value = true;
  try {
    const { data } = await WabaCallingAPI.settings(props.inbox.id, true);
    enabled.value = data.enabled === true;
    managedExternally.value = data.managed_externally === true;
  } catch (error) {
    useAlert(t('ASTRACHAT_WABA_CALLING.ALERT.CONFIG_LOAD_FAILED'));
  } finally {
    loading.value = false;
  }
};

const save = async () => {
  saving.value = true;
  try {
    const { data } = await WabaCallingAPI.updateSettings(props.inbox.id, {
      enabled: enabled.value,
    });
    enabled.value = data.enabled === true;
    useAlert(t('ASTRACHAT_WABA_CALLING.ALERT.SAVED'));
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('ASTRACHAT_WABA_CALLING.ALERT.SAVE_FAILED')
    );
  } finally {
    saving.value = false;
  }
};

onMounted(load);
</script>

<template>
  <div class="py-6">
    <h2 class="text-base font-medium text-n-slate-12">
      {{ t('ASTRACHAT_WABA_CALLING.SETTINGS.TITLE') }}
    </h2>
    <p class="mt-1 text-sm text-n-slate-11">
      {{ t('ASTRACHAT_WABA_CALLING.SETTINGS.DESCRIPTION') }}
    </p>
    <div
      v-if="managedExternally"
      class="mt-4 rounded-lg border border-n-amber-6 bg-n-amber-3 p-3 text-sm text-n-amber-11"
    >
      {{ t('ASTRACHAT_WABA_CALLING.SETTINGS.MANAGED_EXTERNALLY') }}
    </div>
    <label
      class="mt-6 flex items-center gap-3 text-sm font-medium text-n-slate-12"
    >
      <input
        v-model="enabled"
        type="checkbox"
        :disabled="loading || managedExternally"
      />
      {{ t('ASTRACHAT_WABA_CALLING.SETTINGS.ENABLED') }}
    </label>
    <NextButton
      class="mt-5"
      sm
      blue
      :label="t('ASTRACHAT_WABA_CALLING.ACTION.SAVE')"
      :is-loading="saving"
      :disabled="loading || managedExternally"
      @click="save"
    />
  </div>
</template>
